import { createTrackingEmitterHooks } from "@b2bportal/core-tracking";
import type { TrackingProperties } from "@overrides/types";

export type CarsRawEvent =
  | CarsAppLoadedRawEvent
  | CarsSearchLoadedRawEvent
  | CarViewedCalendarRawEvent
  | CarsAvailabilityPageLoadedRawEvent
  | CarsAppliedFilterRawEvent
  | CarsShopLoadedRawEvent
  | ViewedCarShopPageLoadingRawEvent
  | ViewedCarAvailabilityPageLoadingRawEvent
  | ClickedCarOnAvailabilityRawEvent;

export enum CarsRawEventName {
  CarSearchLoaded = "CarSearchLoaded",
  CarViewedCalendar = "CarViewedCalendar",
  CarAppLoaded = "CarAppLoaded",
  CarAvailabilityPageLoaded = "CarAvailabilityPageLoaded",
  CarsAppliedFilter = "CarsAppliedFilter",
  CarShopLoaded = "CarsShopLoaded",
  ViewedCarShopPageLoading = "ViewedCarShopLoading",
  ViewedCarAvailabilityPageLoading = "ViewedCarAvailabilityPageLoading",
  ClickedCarOnAvailability = "ClickedCarOnAvailability",
}

export type CarsSearchLoadedRawEvent = {
  name: CarsRawEventName.CarSearchLoaded;
};

export type CarViewedCalendarRawEvent = {
  name: CarsRawEventName.CarViewedCalendar;
};

export type CarsAppLoadedRawEvent = {
  name: CarsRawEventName.CarAppLoaded;
};

export type CarsAvailabilityPageLoadedRawEvent = {
  name: CarsRawEventName.CarAvailabilityPageLoaded;
};

export type CarsAppliedFilterRawEvent = {
  name: CarsRawEventName.CarsAppliedFilter;
};

export type CarsShopLoadedRawEvent = {
  name: CarsRawEventName.CarShopLoaded;
};

export type ViewedCarShopPageLoadingRawEvent = {
  name: CarsRawEventName.ViewedCarShopPageLoading;
  properties: {
    runTime: number;
  };
};

export type ViewedCarAvailabilityPageLoadingRawEvent = {
  name: CarsRawEventName.ViewedCarAvailabilityPageLoading;
  properties: {
    runTime: number;
  };
};

export type ClickedCarOnAvailabilityRawEvent = {
  name: CarsRawEventName.ClickedCarOnAvailability;
};

export type CarsEntryTrackingProperties = {
  properties: {
    // country
    market: string;
    // if the user selected an airport
    airport: boolean;
    // date of rental start (e.g. 2020-07-01)
    pick_up_date: string;
    // use 24hr time (e.g. 0→23)
    pick_up_hour: string;
    // date of rental end (e.g. 2020-07-01)
    drop_off_date: string;
    // use 24hr time (e.g. 0→23)
    drop_off_hour: string;
    // age entered by the user
    search_age: number;
    // if the pickup and dropoff selected by the user were the same or not
    same_pickUp_dropOff: boolean;
    // days prior to pickup that the reservation is for
    cars_advance: number;
  };
} & { encryptedProperties?: TrackingProperties["encryptedProperties"] };

export type ViewedCarsListTrackingProperties = {
  properties: {
    // how many cars are returned/displayed to the user
    number_of_results: number;
    // price of cheapest result
    cheapest_price: number;
    // provider of cheapest result (ie. ABG or PL)
    cheapest_provider: string;
    // country of pickup location
    pick_up_place_country: string;
    // city of pickup location
    pick_up_place_city: string;
    // unique id for pickup location
    // if this comes back as none, then it is not an airport. if this has a value, it is an airport
    pick_up_place_id: string;
    // country of dropoff location
    drop_off_place_country: string;
    // city of dropoff location
    drop_off_place_city: string;
    // unique id for dropoff location
    drop_off_place_id: string;
  };
} & {
  encryptedProperties?: TrackingProperties["encryptedProperties"];
} & CarsEntryTrackingProperties;

export type AppliedCarsFilterTrackingProperties = {
  properties: {
    // A customer applies a filter. This should be tracked so that we know what amount of usage each filter get
    applied_filter: string;
  };
} & { encryptedProperties?: TrackingProperties["encryptedProperties"] };

export type ViewedCarDetailsTrackingProperties = {
  properties: {
    fuel_policy: string;
    // suv, minivan, full-size, etc
    vehicle_type: string;
    // total price of vehicle in USD
    total_price: number;
    // true/false
    free_cancellation: boolean;
    unlimited_mileage: boolean;
    automatic: boolean;
    supplier: string;
    vendor: string;
    non_refundable: string;
  };
} & { encryptedProperties?: TrackingProperties["encryptedProperties"] };

export const {
  useEmitRawEvent: useEmitApacCarsRawEvent,
  useEmitRawEventOnce: useEmitApacCarsRawEventOnce,
  useEmitBeforeLoad: useEmitApacEventBeforeLoad,
} = createTrackingEmitterHooks<CarsRawEvent>();
